import React, { useCallback, useId, useState, type ReactNode } from 'react';
import {
  BodyContent,
  BodyInner,
  BodyWrapper,
  Bullet,
  Chevron,
  Header,
  Section,
  Subtitle,
  Title,
} from './NodeSection.styled';

export interface NodeSectionProps {
  /** Uppercase section label, e.g. "Execution". */
  title: string;
  /** Small dimmed value shown after a bullet, e.g. "CLOUD" — gives an
   *  affordance for what's collapsed inside without expanding. */
  subtitle?: ReactNode;
  /** Initial open state; collapsed by default. */
  defaultExpanded?: boolean;
  children: ReactNode;
  dataTestId?: string;
}

const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M6 9l6 6 6-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Compact collapsible group for a node card body. Styled to match the card's
 * dense field-label language rather than the heavier `@aibuild/ui`
 * `ExpandableRow` (a full `ListItem`), which would add height instead of
 * saving it.
 */
const NodeSection: React.FC<NodeSectionProps> = ({
  title,
  subtitle,
  defaultExpanded = false,
  children,
  dataTestId = 'node-section',
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const bodyId = useId();

  const toggle = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  }, []);

  const hasSubtitle = subtitle != null && subtitle !== '';

  return (
    <Section data-testid={dataTestId} data-expanded={expanded || undefined}>
      <Header
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={bodyId}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${title}`}
        data-testid={`${dataTestId}__toggle`}
      >
        <Title>{title}</Title>
        {hasSubtitle && (
          <>
            <Bullet aria-hidden="true">•</Bullet>
            <Subtitle data-testid={`${dataTestId}__subtitle`}>{subtitle}</Subtitle>
          </>
        )}
        <Chevron $expanded={expanded} aria-hidden="true">
          <ChevronIcon />
        </Chevron>
      </Header>
      <BodyWrapper
        $expanded={expanded}
        id={bodyId}
        inert={!expanded}
        data-testid={`${dataTestId}__body`}
      >
        <BodyInner>
          <BodyContent>{children}</BodyContent>
        </BodyInner>
      </BodyWrapper>
    </Section>
  );
};

export default NodeSection;
